import { supabase } from '@/lib/supabase';

/**
 * Upvotes Service - Handles all upvote-related operations with Supabase
 */

export const upvotesService = {
  /**
   * Get all upvotes
   * @returns {Promise<Array>}
   */
  async list() {
    const { data, error } = await supabase
      .from('upvotes')
      .select('*')
      .order('created_date', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Filter upvotes by criteria
   * @param {Object} filters - Filter criteria (e.g., { report_id: 'id', user_id: 'uuid' })
   * @param {string} sortBy - Sort field
   * @returns {Promise<Array>}
   */
  async filter(filters = {}, sortBy = '-created_date') {
    const ascending = !sortBy.startsWith('-');
    const field = sortBy.replace('-', '');
    
    let query = supabase.from('upvotes').select('*');

    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      query = query.eq(key, value);
    });

    // Apply sorting
    query = query.order(field, { ascending });

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  },

  /**
   * Get a single upvote by ID
   * @param {string} id - Upvote ID
   * @returns {Promise<Object>}
   */
  async getById(id) {
    const { data, error } = await supabase
      .from('upvotes')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Create a new upvote
   * @param {Object} upvoteData - Upvote data { report_id, user_id }
   * @returns {Promise<Object>}
   */
  async create(upvoteData) {
    const { data, error } = await supabase
      .from('upvotes')
      .insert([{
        ...upvoteData,
        created_date: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) {
      // Handle duplicate upvote error
      if (error.code === '23505') {
        throw new Error('You have already upvoted this report');
      }
      throw error;
    }
    return data;
  },

  /**
   * Delete an upvote
   * @param {string} id - Upvote ID
   * @returns {Promise<void>}
   */
  async delete(id) {
    const { error } = await supabase
      .from('upvotes')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Check if user has upvoted a report
   * @param {string} reportId - Report ID
   * @param {string} userId - User ID
   * @returns {Promise<boolean>}
   */
  async hasUpvoted(reportId, userId) {
    const { data, error } = await supabase
      .from('upvotes')
      .select('id')
      .eq('report_id', reportId)
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
    return !!data;
  },

  /**
   * Toggle upvote (add if not exists, remove if exists)
   * @param {string} reportId - Report ID
   * @param {string} userId - User ID
   * @returns {Promise<{action: 'added'|'removed', upvote: Object|null}>}
   */
  async toggle(reportId, userId) {
    // Check if upvote exists
    const existing = await this.filter({ report_id: reportId, user_id: userId });
    
    if (existing.length > 0) {
      // Remove upvote
      await this.delete(existing[0].id);
      return { action: 'removed', upvote: null };
    } else {
      // Add upvote
      const upvote = await this.create({ report_id: reportId, user_id: userId });
      return { action: 'added', upvote };
    }
  }
};
